import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { doc, getDoc, collection, getDocs, query, where } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '@/lib/firebase';
import { AffiliateCode, Referral, Payout, ServiceLine } from '@/types';
import { Layout } from '@/components/Layout';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import {
  REFERRAL_TIERS,
  BONUSES,
  SERVICE_LINES,
  serviceLineLabel,
  getTier,
  tierLabel,
  computeCommission,
  computeLargeDealBonus,
  fastStartQualifies,
  FAST_START_AMOUNT,
  FAST_START_MIN_DEAL,
  LARGE_DEAL_THRESHOLD,
} from '@/lib/affiliate';

const fmtDate = (d?: { toDate?: () => Date }) => (d?.toDate ? d.toDate().toLocaleDateString() : '—');
const money = (n?: number) => `$${(n || 0).toFixed(2)}`;

function AdminAffiliateDetailContent() {
  const { codeId = '' } = useParams<{ codeId: string }>();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [affiliate, setAffiliate] = useState<AffiliateCode | null>(null);
  const [referrals, setReferrals] = useState<Referral[]>([]);
  const [payouts, setPayouts] = useState<Payout[]>([]);
  const [notFound, setNotFound] = useState(false);

  const [actionError, setActionError] = useState<string | null>(null);
  const [actionSuccess, setActionSuccess] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // Profile editing
  const [editingProfile, setEditingProfile] = useState(false);
  const [profileWallet, setProfileWallet] = useState('');
  const [profileXUsername, setProfileXUsername] = useState('');

  // Add referral form
  const [refEmail, setRefEmail] = useState('');
  const [refType, setRefType] = useState(REFERRAL_TIERS[0].key);
  const [refServiceLine, setRefServiceLine] = useState<ServiceLine>('WEBSITE');
  const [refProject, setRefProject] = useState('');
  const [refValue, setRefValue] = useState(0);
  const [refRate, setRefRate] = useState(REFERRAL_TIERS[0].rate);
  const [refDate, setRefDate] = useState('');

  // Edit referral modal
  const [editingReferral, setEditingReferral] = useState<Referral | null>(null);
  const [editRefEmail, setEditRefEmail] = useState('');
  const [editRefType, setEditRefType] = useState(REFERRAL_TIERS[0].key);
  const [editRefServiceLine, setEditRefServiceLine] = useState<ServiceLine>('WEBSITE');
  const [editRefProject, setEditRefProject] = useState('');
  const [editRefValue, setEditRefValue] = useState(0);
  const [editRefRate, setEditRefRate] = useState(REFERRAL_TIERS[0].rate);

  // Payout modal
  const [payoutOpen, setPayoutOpen] = useState(false);
  const [payoutAmount, setPayoutAmount] = useState(0);
  const [payoutTxHash, setPayoutTxHash] = useState('');
  const [payoutNote, setPayoutNote] = useState('');
  const [payoutDate, setPayoutDate] = useState('');

  // Bonus modal
  const [bonusOpen, setBonusOpen] = useState(false);
  const [bonusLabel, setBonusLabel] = useState(BONUSES[0].label);
  const [bonusAmount, setBonusAmount] = useState(0);
  const [bonusProjectValue, setBonusProjectValue] = useState(0);
  const [bonusNote, setBonusNote] = useState('');
  const [bonusDate, setBonusDate] = useState('');

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [codeId]);

  const fetchData = async () => {
    if (!codeId) return;
    try {
      setLoading(true);
      const codeSnap = await getDoc(doc(db, 'affiliateCodes', codeId));
      if (!codeSnap.exists()) {
        setNotFound(true);
        return;
      }
      const code = { id: codeSnap.id, ...codeSnap.data() } as AffiliateCode;
      setAffiliate(code);

      const refsSnap = await getDocs(
        query(collection(db, 'referrals'), where('affiliateUserId', '==', code.userId))
      );
      const refsData = refsSnap.docs.map((d) => ({ id: d.id, ...d.data() })) as Referral[];
      refsData.sort((a, b) => (b.createdAt?.toMillis?.() || 0) - (a.createdAt?.toMillis?.() || 0));
      setReferrals(refsData);

      const poutsSnap = await getDocs(
        query(collection(db, 'payouts'), where('affiliateUserId', '==', code.userId))
      );
      const poutsData = poutsSnap.docs.map((d) => ({ id: d.id, ...d.data() })) as Payout[];
      poutsData.sort((a, b) => (b.paidAt?.toMillis?.() || 0) - (a.paidAt?.toMillis?.() || 0));
      setPayouts(poutsData);
    } catch (err) {
      console.error('Error loading affiliate:', err);
      setActionError('Failed to load affiliate data');
    } finally {
      setLoading(false);
    }
  };

  const call = async (name: string, payload: object, successMsg: string) => {
    setBusy(true);
    setActionError(null);
    try {
      await httpsCallable(functions, name)(payload);
      setActionSuccess(successMsg);
      await fetchData();
      return true;
    } catch (err: any) {
      setActionError(err.message || 'Action failed');
      return false;
    } finally {
      setBusy(false);
    }
  };

  const openProfileEdit = () => {
    if (!affiliate) return;
    setProfileWallet(affiliate.xrplWallet);
    setProfileXUsername(affiliate.xUsername);
    setEditingProfile(true);
    setActionError(null);
    setActionSuccess(null);
  };

  const saveProfile = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!affiliate) return;
    const ok = await call(
      'adminUpdateAffiliate',
      { codeId: affiliate.id, xrplWallet: profileWallet.trim(), xUsername: profileXUsername.trim() },
      'Profile updated'
    );
    if (ok) setEditingProfile(false);
  };

  const onSelectType = (key: string, setType: (k: any) => void, setRate: (n: number) => void) => {
    setType(key);
    const tier = getTier(key);
    if (tier) setRate(tier.rate);
  };

  const addReferral = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!affiliate || !refEmail.trim()) return;
    const ok = await call(
      'adminAddReferral',
      {
        codeId: affiliate.id,
        referredEmail: refEmail.trim(),
        referralType: refType,
        serviceLine: refServiceLine,
        projectName: refProject.trim() || undefined,
        projectValue: refValue,
        commissionRate: refRate,
        occurredAt: refDate ? new Date(refDate).toISOString() : undefined,
      },
      `Referral logged for ${refEmail.trim()} — ${money(computeCommission(refValue, refRate))} commission`
    );
    if (ok) {
      setRefEmail('');
      setRefProject('');
      setRefValue(0);
      setRefType(REFERRAL_TIERS[0].key);
      setRefServiceLine('WEBSITE');
      setRefRate(REFERRAL_TIERS[0].rate);
      setRefDate('');
    }
  };

  const openEditReferral = (r: Referral) => {
    setEditingReferral(r);
    setEditRefEmail(r.referredEmail);
    setEditRefType((r.referralType as any) || REFERRAL_TIERS[0].key);
    setEditRefServiceLine((r.serviceLine as ServiceLine) || 'WEBSITE');
    setEditRefProject(r.projectName || '');
    setEditRefValue(r.projectValue || 0);
    setEditRefRate(r.commissionRate ?? getTier(r.referralType)?.rate ?? 0);
    setActionError(null);
    setActionSuccess(null);
  };

  const saveReferral = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingReferral) return;
    const ok = await call(
      'adminUpdateReferral',
      {
        referralId: editingReferral.id,
        referredEmail: editRefEmail.trim(),
        referralType: editRefType,
        serviceLine: editRefServiceLine,
        projectName: editRefProject.trim() || undefined,
        projectValue: editRefValue,
        commissionRate: editRefRate,
      },
      'Referral updated'
    );
    if (ok) setEditingReferral(null);
  };

  const deleteReferral = async (r: Referral) => {
    if (!confirm(`Delete the referral for ${r.referredEmail}? This adjusts the affiliate's totals.`)) return;
    await call('adminDeleteReferral', { referralId: r.id }, 'Referral deleted');
  };

  const openPayout = () => {
    if (!affiliate) return;
    setPayoutAmount(Math.max(0, (affiliate.totalEarnings || 0) - (affiliate.totalPaid || 0)));
    setPayoutTxHash('');
    setPayoutNote('');
    setPayoutDate('');
    setPayoutOpen(true);
    setActionError(null);
    setActionSuccess(null);
  };

  const recordPayout = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!affiliate || payoutAmount <= 0) return;
    const ok = await call(
      'adminRecordPayout',
      {
        codeId: affiliate.id,
        amount: payoutAmount,
        txHash: payoutTxHash.trim() || undefined,
        note: payoutNote.trim() || undefined,
        paidAt: payoutDate ? new Date(payoutDate).toISOString() : undefined,
      },
      `Recorded $${payoutAmount.toFixed(2)} payout`
    );
    if (ok) setPayoutOpen(false);
  };

  const openBonus = () => {
    if (!affiliate) return;
    setBonusLabel(BONUSES[0].label);
    setBonusAmount(0);
    setBonusProjectValue(0);
    setBonusNote('');
    setBonusDate('');
    setBonusOpen(true);
    setActionError(null);
    setActionSuccess(null);
  };

  // Computed Large Deal Bonus for the current modal inputs (preview only).
  const largeDealPreview = computeLargeDealBonus(bonusProjectValue, 'CLOSING_PARTNER');

  const addBonus = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!affiliate) return;
    const occurredAt = bonusDate ? new Date(bonusDate).toISOString() : undefined;

    if (bonusLabel === 'Large Deal Bonus') {
      if (largeDealPreview <= 0) {
        setActionError('Large Deal Bonus applies only to Active Closing Partner deals over $25,000');
        return;
      }
      const ok = await call(
        'adminApplyDealBonus',
        {
          codeId: affiliate.id,
          bonusType: 'LARGE_DEAL',
          projectValue: bonusProjectValue,
          referralType: 'CLOSING_PARTNER',
          note: bonusNote.trim() || undefined,
          occurredAt,
        },
        `Large Deal Bonus applied — ${money(largeDealPreview)}`
      );
      if (ok) setBonusOpen(false);
      return;
    }

    if (bonusLabel === 'Fast Start Bonus') {
      if (!fastStartQualifies(bonusProjectValue)) {
        setActionError('Fast Start Bonus requires a first closed deal over $2,500');
        return;
      }
      const ok = await call(
        'adminApplyDealBonus',
        {
          codeId: affiliate.id,
          bonusType: 'FAST_START',
          projectValue: bonusProjectValue,
          note: bonusNote.trim() || undefined,
          occurredAt,
        },
        `Fast Start Bonus applied — ${money(FAST_START_AMOUNT)}`
      );
      if (ok) setBonusOpen(false);
      return;
    }

    // Manual Adjustment — free-form amount.
    if (bonusAmount <= 0) return;
    const ok = await call(
      'adminAddBonus',
      {
        codeId: affiliate.id,
        label: 'Manual Adjustment',
        amount: bonusAmount,
        note: bonusNote.trim() || undefined,
        occurredAt,
      },
      `Added Manual Adjustment — ${money(bonusAmount)}`
    );
    if (ok) setBonusOpen(false);
  };

  const deletePayout = async (p: Payout) => {
    if (!confirm(`Delete the $${(p.amount || 0).toFixed(2)} payout? This rolls back the paid total.`)) return;
    await call('adminDeletePayout', { payoutId: p.id }, 'Payout deleted');
  };

  if (loading) {
    return (
      <Layout>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
          <LoadingSpinner />
        </div>
      </Layout>
    );
  }

  if (notFound || !affiliate) {
    return (
      <Layout>
        <div style={{ maxWidth: '720px', margin: '0 auto' }}>
          <div className="terminal-card">
            <div className="card-header"><span className="card-num">ERR</span> AFFILIATE NOT FOUND</div>
            <div className="card-body">
              <p style={{ color: 'var(--muted)', marginBottom: '1rem' }}>No affiliate code matches "{codeId}".</p>
              <button onClick={() => navigate('/admin/affiliates')} className="action-btn">Back to Affiliates</button>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  const earned = affiliate.totalEarnings || 0;
  const paid = affiliate.totalPaid || 0;
  const pending = earned - paid;

  return (
    <Layout>
      <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
        <Link to="/admin/affiliates" style={{ color: 'var(--muted)', textDecoration: 'none', fontSize: '0.75rem', letterSpacing: '0.1em' }}>
          ← Back to Affiliates
        </Link>

        {/* Feedback */}
        {actionSuccess && (
          <div className="terminal-alert success" style={{ margin: '1rem 0' }}>
            <span>{actionSuccess}</span>
            <button onClick={() => setActionSuccess(null)} style={{ background: 'none', border: 'none', color: 'var(--success)', cursor: 'pointer', marginLeft: 'auto' }}>✕</button>
          </div>
        )}
        {actionError && (
          <div className="terminal-alert error" style={{ margin: '1rem 0' }}>
            <span>{actionError}</span>
            <button onClick={() => setActionError(null)} style={{ background: 'none', border: 'none', color: 'var(--danger)', cursor: 'pointer', marginLeft: 'auto' }}>✕</button>
          </div>
        )}

        {/* Account header */}
        <div className="terminal-card" style={{ marginTop: '1rem' }}>
          <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span><span className="card-num">AC</span> AFFILIATE ACCOUNT</span>
            <button onClick={openProfileEdit} className="action-btn secondary" style={{ padding: '0.25rem 0.625rem', fontSize: '0.7rem' }}>EDIT PROFILE</button>
          </div>
          <div className="card-body">
            <div style={{ fontFamily: 'Consolas, monospace', color: 'var(--gold)', fontWeight: 700, fontSize: '1.25rem', marginBottom: '1rem', wordBreak: 'break-all' }}>
              {affiliate.code}
            </div>
            <div className="terminal-grid" style={{ gridTemplateColumns: 'repeat(2, 1fr)' }}>
              <div className="field-group">
                <div className="field-label">EMAIL</div>
                <div style={{ fontSize: '0.8rem', color: 'var(--fg)', padding: '4px 0' }}>{affiliate.userEmail}</div>
              </div>
              <div className="field-group">
                <div className="field-label">X USERNAME</div>
                <div style={{ fontSize: '0.8rem', padding: '4px 0' }}>
                  <a href={`https://twitter.com/${affiliate.xUsername}`} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--gold)', textDecoration: 'none' }}>@{affiliate.xUsername}</a>
                </div>
              </div>
              <div className="field-group">
                <div className="field-label">XRPL WALLET</div>
                <div style={{ fontSize: '0.7rem', color: 'var(--fg)', fontFamily: 'Consolas, monospace', padding: '4px 0', wordBreak: 'break-all' }}>{affiliate.xrplWallet}</div>
              </div>
              <div className="field-group">
                <div className="field-label">CODE CREATED</div>
                <div style={{ fontSize: '0.8rem', color: 'var(--fg)', padding: '4px 0' }}>{fmtDate(affiliate.createdAt)}</div>
              </div>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="terminal-grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
          <div className="terminal-card">
            <div className="card-header"><span className="card-num">S1</span> REFERRALS</div>
            <div className="card-body" style={{ fontSize: '1.75rem', fontWeight: 700, color: 'var(--gold)' }}>{affiliate.totalReferrals || 0}</div>
          </div>
          <div className="terminal-card">
            <div className="card-header"><span className="card-num">S2</span> EARNED</div>
            <div className="card-body" style={{ fontSize: '1.75rem', fontWeight: 700, color: 'var(--success)' }}>${earned.toFixed(2)}</div>
          </div>
          <div className="terminal-card">
            <div className="card-header"><span className="card-num">S3</span> PAID</div>
            <div className="card-body" style={{ fontSize: '1.75rem', fontWeight: 700, color: 'var(--muted)' }}>${paid.toFixed(2)}</div>
          </div>
          <div className="terminal-card">
            <div className="card-header"><span className="card-num">S4</span> PENDING</div>
            <div className="card-body" style={{ fontSize: '1.75rem', fontWeight: 700, color: pending > 0 ? 'var(--gold)' : 'var(--muted)' }}>${pending.toFixed(2)}</div>
          </div>
        </div>

        {/* Referrals */}
        <div className="terminal-card" style={{ marginTop: '2rem' }}>
          <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span><span className="card-num">R1</span> REFERRALS — LOG A CLOSED DEAL</span>
            <button onClick={openBonus} className="action-btn secondary" style={{ padding: '0.25rem 0.625rem', fontSize: '0.7rem' }}>+ BONUS / ADJUSTMENT</button>
          </div>
          <div className="card-body">
            <form onSubmit={addReferral} style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div className="field-group" style={{ flex: '2 1 200px' }}>
                <label className="field-label">REFERRED CLIENT</label>
                <input type="text" required value={refEmail} onChange={(e) => setRefEmail(e.target.value)} className="terminal-input" placeholder="name / company / email" />
              </div>
              <div className="field-group" style={{ flex: '1 1 180px' }}>
                <label className="field-label">REFERRAL TYPE</label>
                <select value={refType} onChange={(e) => onSelectType(e.target.value, setRefType, setRefRate)} className="terminal-input">
                  {REFERRAL_TIERS.map((t) => (
                    <option key={t.key} value={t.key}>{t.label} ({t.rate}%)</option>
                  ))}
                </select>
              </div>
              <div className="field-group" style={{ flex: '1 1 160px' }}>
                <label className="field-label">SERVICE</label>
                <select value={refServiceLine} onChange={(e) => setRefServiceLine(e.target.value as ServiceLine)} className="terminal-input">
                  {SERVICE_LINES.map((s) => (
                    <option key={s.key} value={s.key}>{s.label}</option>
                  ))}
                </select>
              </div>
              <div className="field-group" style={{ flex: '2 1 200px' }}>
                <label className="field-label">PROJECT (OPTIONAL)</label>
                <input type="text" value={refProject} onChange={(e) => setRefProject(e.target.value)} className="terminal-input" placeholder="e.g. website build" />
              </div>
              <div className="field-group" style={{ flex: '1 1 140px' }}>
                <label className="field-label">COLLECTED VALUE ($)</label>
                <input type="number" min="0" step="0.01" value={refValue} onChange={(e) => setRefValue(Math.max(0, parseFloat(e.target.value) || 0))} className="terminal-input" />
              </div>
              <div className="field-group" style={{ flex: '0 1 90px' }}>
                <label className="field-label">RATE (%)</label>
                <input type="number" min="0" max="100" step="0.5" value={refRate} onChange={(e) => setRefRate(Math.max(0, Math.min(100, parseFloat(e.target.value) || 0)))} className="terminal-input" />
              </div>
              <div className="field-group" style={{ flex: '1 1 130px' }}>
                <label className="field-label">DATE (OPTIONAL)</label>
                <input type="date" value={refDate} onChange={(e) => setRefDate(e.target.value)} className="terminal-input" />
              </div>
              <button type="submit" className="action-btn" disabled={busy || !refEmail.trim()} style={{ flex: '0 0 auto' }}>
                {busy ? 'SAVING...' : `+ ADD (${money(computeCommission(refValue, refRate))})`}
              </button>
            </form>
            <div style={{ fontSize: '0.7rem', color: 'var(--muted)', marginTop: '0.5rem' }}>
              Commission = collected value × rate. Enter revenue GRID actually collected, not quotes. For recurring work, log each month (5%, up to 12).
            </div>
          </div>
          <div className="table-wrap">
            <table className="terminal-table">
              <thead>
                <tr>
                  <th>REFERRED</th>
                  <th>TYPE</th>
                  <th>SERVICE</th>
                  <th>PROJECT</th>
                  <th>VALUE</th>
                  <th>RATE</th>
                  <th>COMMISSION</th>
                  <th>DATE</th>
                  <th>ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {referrals.length === 0 ? (
                  <tr><td colSpan={9} style={{ textAlign: 'center', color: 'var(--muted)', padding: '2rem' }}>NO REFERRALS YET</td></tr>
                ) : (
                  referrals.map((r) => (
                    <tr key={r.id}>
                      <td style={{ color: 'var(--fg)' }}>{r.isBonus ? (r.bonusLabel || r.referredEmail) : r.referredEmail}</td>
                      <td style={{ fontSize: '0.75rem' }}>{r.isBonus ? <span className="term-badge medium">BONUS</span> : r.referralType ? tierLabel(r.referralType) : <span className="term-badge">{r.manual ? 'MANUAL' : 'SIGNUP'}</span>}</td>
                      <td style={{ color: 'var(--muted)', fontSize: '0.75rem' }}>{r.isBonus ? '—' : serviceLineLabel(r.serviceLine)}</td>
                      <td style={{ color: 'var(--muted)', fontSize: '0.75rem' }}>{r.projectName || '—'}</td>
                      <td style={{ color: 'var(--muted)' }}>{r.projectValue ? money(r.projectValue) : '—'}</td>
                      <td style={{ color: 'var(--muted)' }}>{r.commissionRate != null ? `${r.commissionRate}%` : '—'}</td>
                      <td style={{ color: r.earningsAmount ? 'var(--success)' : 'var(--muted)', fontWeight: 700 }}>{money(r.earningsAmount)}</td>
                      <td style={{ color: 'var(--muted)', fontSize: '0.75rem' }}>{fmtDate(r.createdAt)}</td>
                      <td>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <button onClick={() => openEditReferral(r)} className="action-btn secondary" style={{ padding: '0.25rem 0.625rem', fontSize: '0.7rem' }}>EDIT</button>
                          <button onClick={() => deleteReferral(r)} className="action-btn secondary" style={{ padding: '0.25rem 0.5rem', fontSize: '0.7rem' }}>✕</button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Payouts */}
        <div className="terminal-card" style={{ marginTop: '2rem' }}>
          <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span><span className="card-num">P1</span> PAYOUTS</span>
            <button onClick={openPayout} className="action-btn" style={{ padding: '0.25rem 0.625rem', fontSize: '0.7rem' }}>+ RECORD PAYOUT</button>
          </div>
          <div className="table-wrap">
            <table className="terminal-table">
              <thead>
                <tr>
                  <th>DATE</th>
                  <th>AMOUNT</th>
                  <th>TX HASH</th>
                  <th>NOTE</th>
                  <th>ACTIONS</th>
                </tr>
              </thead>
              <tbody>
                {payouts.length === 0 ? (
                  <tr><td colSpan={5} style={{ textAlign: 'center', color: 'var(--muted)', padding: '2rem' }}>NO PAYOUTS RECORDED</td></tr>
                ) : (
                  payouts.map((p) => (
                    <tr key={p.id}>
                      <td style={{ color: 'var(--muted)', fontSize: '0.75rem' }}>{fmtDate(p.paidAt)}</td>
                      <td style={{ color: 'var(--success)', fontWeight: 700 }}>${(p.amount || 0).toFixed(2)}</td>
                      <td style={{ fontFamily: 'Consolas, monospace', fontSize: '0.7rem', color: 'var(--muted)', wordBreak: 'break-all' }}>{p.txHash || '—'}</td>
                      <td style={{ color: 'var(--muted)', fontSize: '0.75rem' }}>{p.note || '—'}</td>
                      <td>
                        <button onClick={() => deletePayout(p)} className="action-btn secondary" style={{ padding: '0.25rem 0.5rem', fontSize: '0.7rem' }}>✕</button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Edit Profile Modal */}
      {editingProfile && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: '1rem' }}>
          <div className="terminal-card" style={{ maxWidth: '440px', width: '100%' }}>
            <div className="card-header"><span className="card-num">EP</span> EDIT PROFILE</div>
            <div className="card-body">
              <form onSubmit={saveProfile}>
                <div className="field-group" style={{ marginBottom: '1rem' }}>
                  <label className="field-label">X USERNAME</label>
                  <input type="text" value={profileXUsername} onChange={(e) => setProfileXUsername(e.target.value)} className="terminal-input" />
                </div>
                <div className="field-group" style={{ marginBottom: '1.5rem' }}>
                  <label className="field-label">XRPL WALLET</label>
                  <input type="text" value={profileWallet} onChange={(e) => setProfileWallet(e.target.value)} className="terminal-input" style={{ fontFamily: 'Consolas, monospace' }} />
                </div>
                <div className="btn-row">
                  <button type="button" onClick={() => setEditingProfile(false)} className="action-btn secondary" disabled={busy}>CANCEL</button>
                  <button type="submit" className="action-btn" disabled={busy}>{busy ? 'SAVING...' : 'SAVE'}</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Edit Referral Modal */}
      {editingReferral && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: '1rem' }}>
          <div className="terminal-card" style={{ maxWidth: '420px', width: '100%' }}>
            <div className="card-header"><span className="card-num">ER</span> EDIT REFERRAL</div>
            <div className="card-body">
              <form onSubmit={saveReferral}>
                <div className="field-group" style={{ marginBottom: '1rem' }}>
                  <label className="field-label">REFERRED CLIENT</label>
                  <input type="text" required value={editRefEmail} onChange={(e) => setEditRefEmail(e.target.value)} className="terminal-input" />
                </div>
                <div className="field-group" style={{ marginBottom: '1rem' }}>
                  <label className="field-label">REFERRAL TYPE</label>
                  <select value={editRefType} onChange={(e) => onSelectType(e.target.value, setEditRefType, setEditRefRate)} className="terminal-input">
                    {REFERRAL_TIERS.map((t) => (
                      <option key={t.key} value={t.key}>{t.label} ({t.rate}%)</option>
                    ))}
                  </select>
                </div>
                <div className="field-group" style={{ marginBottom: '1rem' }}>
                  <label className="field-label">SERVICE</label>
                  <select value={editRefServiceLine} onChange={(e) => setEditRefServiceLine(e.target.value as ServiceLine)} className="terminal-input">
                    {SERVICE_LINES.map((s) => (
                      <option key={s.key} value={s.key}>{s.label}</option>
                    ))}
                  </select>
                </div>
                <div className="field-group" style={{ marginBottom: '1rem' }}>
                  <label className="field-label">PROJECT (OPTIONAL)</label>
                  <input type="text" value={editRefProject} onChange={(e) => setEditRefProject(e.target.value)} className="terminal-input" />
                </div>
                <div style={{ display: 'flex', gap: '0.75rem' }}>
                  <div className="field-group" style={{ flex: 1, marginBottom: '1rem' }}>
                    <label className="field-label">COLLECTED VALUE ($)</label>
                    <input type="number" min="0" step="0.01" value={editRefValue} onChange={(e) => setEditRefValue(Math.max(0, parseFloat(e.target.value) || 0))} className="terminal-input" />
                  </div>
                  <div className="field-group" style={{ flex: '0 0 90px', marginBottom: '1rem' }}>
                    <label className="field-label">RATE (%)</label>
                    <input type="number" min="0" max="100" step="0.5" value={editRefRate} onChange={(e) => setEditRefRate(Math.max(0, Math.min(100, parseFloat(e.target.value) || 0)))} className="terminal-input" />
                  </div>
                </div>
                <div style={{ fontSize: '0.8rem', color: 'var(--muted)', marginBottom: '1.5rem' }}>
                  Commission: <span style={{ color: 'var(--success)', fontWeight: 700 }}>{money(computeCommission(editRefValue, editRefRate))}</span>
                </div>
                <div className="btn-row">
                  <button type="button" onClick={() => setEditingReferral(null)} className="action-btn secondary" disabled={busy}>CANCEL</button>
                  <button type="submit" className="action-btn" disabled={busy}>{busy ? 'SAVING...' : 'SAVE'}</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Record Payout Modal */}
      {payoutOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: '1rem' }}>
          <div className="terminal-card" style={{ maxWidth: '420px', width: '100%' }}>
            <div className="card-header"><span className="card-num">PO</span> RECORD PAYOUT</div>
            <div className="card-body">
              <div style={{ fontSize: '0.75rem', color: 'var(--muted)', marginBottom: '0.5rem' }}>
                Earned ${earned.toFixed(2)} · Paid ${paid.toFixed(2)} · Pending <span style={{ color: 'var(--gold)' }}>${pending.toFixed(2)}</span>
              </div>
              <div style={{ fontSize: '0.7rem', color: 'var(--muted)', fontFamily: 'Consolas, monospace', wordBreak: 'break-all', marginBottom: '1rem' }}>
                WALLET: {affiliate.xrplWallet}
              </div>
              <form onSubmit={recordPayout}>
                <div className="field-group" style={{ marginBottom: '1rem' }}>
                  <label className="field-label">AMOUNT PAID ($)</label>
                  <input type="number" min="0.01" step="0.01" required value={payoutAmount} onChange={(e) => setPayoutAmount(Math.max(0, parseFloat(e.target.value) || 0))} className="terminal-input" />
                </div>
                <div className="field-group" style={{ marginBottom: '1rem' }}>
                  <label className="field-label">XRPL TX HASH (OPTIONAL)</label>
                  <input type="text" value={payoutTxHash} onChange={(e) => setPayoutTxHash(e.target.value)} className="terminal-input" placeholder="Transaction hash" style={{ fontFamily: 'Consolas, monospace' }} />
                </div>
                <div className="field-group" style={{ marginBottom: '1rem' }}>
                  <label className="field-label">DATE PAID (OPTIONAL — DEFAULTS TO NOW)</label>
                  <input type="date" value={payoutDate} onChange={(e) => setPayoutDate(e.target.value)} className="terminal-input" />
                </div>
                <div className="field-group" style={{ marginBottom: '1.5rem' }}>
                  <label className="field-label">NOTE (OPTIONAL)</label>
                  <input type="text" value={payoutNote} onChange={(e) => setPayoutNote(e.target.value)} className="terminal-input" placeholder="e.g. paid in RLUSD" />
                </div>
                <div className="btn-row">
                  <button type="button" onClick={() => setPayoutOpen(false)} className="action-btn secondary" disabled={busy}>CANCEL</button>
                  <button type="submit" className="action-btn" disabled={busy || payoutAmount <= 0}>{busy ? 'SAVING...' : 'RECORD PAYOUT'}</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Record Bonus / Adjustment Modal */}
      {bonusOpen && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: '1rem' }}>
          <div className="terminal-card" style={{ maxWidth: '440px', width: '100%' }}>
            <div className="card-header"><span className="card-num">BN</span> APPLY BONUS / ADJUSTMENT</div>
            <div className="card-body">
              <div style={{ fontSize: '0.75rem', color: 'var(--muted)', marginBottom: '0.75rem' }}>
                Adds to earnings but is not counted as a referral. Rule-based bonuses are computed and validated automatically.
              </div>
              <form onSubmit={addBonus}>
                <div className="field-group" style={{ marginBottom: '1rem' }}>
                  <label className="field-label">BONUS TYPE</label>
                  <select value={bonusLabel} onChange={(e) => setBonusLabel(e.target.value)} className="terminal-input">
                    {BONUSES.map((b) => (
                      <option key={b.label} value={b.label}>{b.label}</option>
                    ))}
                    <option value="Manual Adjustment">Manual Adjustment</option>
                  </select>
                </div>

                {/* Large Deal Bonus — Active Closing Partner, >$25k → extra 2% above $25k */}
                {bonusLabel === 'Large Deal Bonus' && (
                  <>
                    <div className="field-group" style={{ marginBottom: '0.5rem' }}>
                      <label className="field-label">COLLECTED PROJECT VALUE ($)</label>
                      <input type="number" min="0" step="0.01" required value={bonusProjectValue}
                        onChange={(e) => setBonusProjectValue(Math.max(0, parseFloat(e.target.value) || 0))}
                        className="terminal-input" placeholder="e.g. 50000" />
                    </div>
                    <div style={{ fontSize: '0.72rem', color: 'var(--muted)', marginBottom: '1rem', lineHeight: 1.5 }}>
                      Active Closing Partner deals only. Bonus = 2% of revenue above ${LARGE_DEAL_THRESHOLD.toLocaleString()}.
                      {' '}Computed bonus:{' '}
                      <strong style={{ color: largeDealPreview > 0 ? 'var(--success)' : 'var(--danger)' }}>
                        {largeDealPreview > 0 ? money(largeDealPreview) : 'Not eligible (deal must exceed $25,000)'}
                      </strong>
                    </div>
                  </>
                )}

                {/* Fast Start Bonus — first closed deal over $2,500 → one-time $250 */}
                {bonusLabel === 'Fast Start Bonus' && (
                  <>
                    <div className="field-group" style={{ marginBottom: '0.5rem' }}>
                      <label className="field-label">FIRST DEAL VALUE ($)</label>
                      <input type="number" min="0" step="0.01" required value={bonusProjectValue}
                        onChange={(e) => setBonusProjectValue(Math.max(0, parseFloat(e.target.value) || 0))}
                        className="terminal-input" placeholder="e.g. 5000" />
                    </div>
                    <div style={{ fontSize: '0.72rem', color: 'var(--muted)', marginBottom: '1rem', lineHeight: 1.5 }}>
                      One-time, on the affiliate's first closed referral over ${FAST_START_MIN_DEAL.toLocaleString()}.
                      {' '}Fixed bonus:{' '}
                      <strong style={{ color: fastStartQualifies(bonusProjectValue) ? 'var(--success)' : 'var(--danger)' }}>
                        {fastStartQualifies(bonusProjectValue) ? money(FAST_START_AMOUNT) : 'Not eligible (deal must exceed $2,500)'}
                      </strong>
                    </div>
                  </>
                )}

                {/* Manual Adjustment — free-form amount */}
                {bonusLabel === 'Manual Adjustment' && (
                  <div className="field-group" style={{ marginBottom: '1rem' }}>
                    <label className="field-label">AMOUNT ($)</label>
                    <input type="number" min="0.01" step="0.01" required value={bonusAmount}
                      onChange={(e) => setBonusAmount(Math.max(0, parseFloat(e.target.value) || 0))}
                      className="terminal-input" />
                  </div>
                )}

                <div className="field-group" style={{ marginBottom: '1rem' }}>
                  <label className="field-label">DATE (OPTIONAL)</label>
                  <input type="date" value={bonusDate} onChange={(e) => setBonusDate(e.target.value)} className="terminal-input" />
                </div>
                <div className="field-group" style={{ marginBottom: '1.5rem' }}>
                  <label className="field-label">NOTE (OPTIONAL)</label>
                  <input type="text" value={bonusNote} onChange={(e) => setBonusNote(e.target.value)} className="terminal-input" placeholder="e.g. $50k platform build" />
                </div>
                <div className="btn-row">
                  <button type="button" onClick={() => setBonusOpen(false)} className="action-btn secondary" disabled={busy}>CANCEL</button>
                  <button type="submit" className="action-btn" disabled={busy}>{busy ? 'SAVING...' : 'APPLY BONUS'}</button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

export function AdminAffiliateDetailPage() {
  return (
    <ProtectedRoute requireAdmin>
      <AdminAffiliateDetailContent />
    </ProtectedRoute>
  );
}
